'use no memo';

import type { MeetingAttendanceStatus, MeetingPublic } from '@repo/types/meetings';
import type { UserPublic } from '@repo/types/users';

import { useGetGroupAttendance } from '@repo/api/group/useGetGroupAttendance';
import { endOfDay, parseISO, startOfDay, subMonths } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';

import type { DateRangeFilterValue } from '@/components/features/filters/DateRangePicker';
import type { AttendanceRow } from '@/components/features/groups/attendance/AttendanceTable';

import DateRangePicker from '@/components/features/filters/DateRangePicker';
import AttendanceTable, { AttendanceTableSkeleton, useAttendanceColumns } from '@/components/features/groups/attendance/AttendanceTable';
import GroupPageErrorState from '@/components/features/groups/layout/GroupPageErrorState';
import { useTanstackTable } from '@/components/features/manage/tanstack-table/useTanstackTable';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/input';

function buildRows(users: UserPublic[], meetings: MeetingPublic[], attendanceMap: Map<number, Map<number, MeetingAttendanceStatus>>): AttendanceRow[] {
  return users
    .map((user) => {
      const statusByMeetingId: Record<number, MeetingAttendanceStatus> = {};
      const totals: Record<MeetingAttendanceStatus, number> = {
        present: 0,
        absent: 0,
        late: 0,
      };
      for (const meeting of meetings) {
        const status = attendanceMap.get(meeting.id)?.get(user.id);
        if (status) {
          statusByMeetingId[meeting.id] = status;
          totals[status] += 1;
        }
      }
      return { user, statusByMeetingId, totals };
    })
    .sort((a, b) => `${a.user.surname} ${a.user.name}`.toLowerCase().localeCompare(`${b.user.surname} ${b.user.name}`.toLowerCase()));
}

function filterByDateRange(meetings: MeetingPublic[], range: DateRangeFilterValue | undefined): MeetingPublic[] {
  if (!range?.start && !range?.end) return meetings;

  const rangeStart = range.start ? startOfDay(new Date(range.start)) : undefined;
  const rangeEnd = range.end ? endOfDay(new Date(range.end)) : undefined;

  return meetings.filter((m) => {
    const d = parseISO(m.startDate);
    if (rangeStart && d < rangeStart) return false;
    if (rangeEnd && d > rangeEnd) return false;
    return true;
  });
}

export default function GroupAttendancePage() {
  const { id } = useParams();
  const groupId = id ? Number(id) : Number.NaN;
  const hasValidGroupId = Number.isFinite(groupId) && groupId > 0;

  const { data, isPending, isError, errorMessage } = useGetGroupAttendance(hasValidGroupId ? groupId : undefined, hasValidGroupId);

  const allMeetings = data?.meetings ?? [];

  const [dateRange, setDateRange] = useState<DateRangeFilterValue | undefined>({
    start: startOfDay(subMonths(new Date(), 1)),
    end: endOfDay(new Date()),
  });

  const filteredMeetings = useMemo<Array<MeetingPublic>>(() => filterByDateRange(allMeetings, dateRange), [allMeetings, dateRange]);

  const attendanceMap = useMemo<Map<number, Map<number, MeetingAttendanceStatus>>>(() => {
    const map = new Map<number, Map<number, MeetingAttendanceStatus>>();
    for (const a of data?.attendances ?? []) {
      if (!a.status) continue;
      const byMeeting = map.get(a.meetingId);
      if (byMeeting) {
        byMeeting.set(a.userId, a.status);
      } else {
        map.set(a.meetingId, new Map([[a.userId, a.status]]));
      }
    }
    return map;
  }, [data?.attendances]);

  const rows = useMemo<Array<AttendanceRow>>(() => buildRows(data?.users ?? [], filteredMeetings, attendanceMap), [data?.users, filteredMeetings, attendanceMap]);

  const tableName = hasValidGroupId ? `group-attendance-${groupId}` : 'group-attendance';
  const columns = useAttendanceColumns(filteredMeetings);

  const { table, setGlobalFilter } = useTanstackTable({
    name: tableName,
    columns,
    data: rows,
    getRowId: (row) => String(row.user.id),
  });

  const applyStartToTodayRange = () => {
    if (allMeetings.length === 0) return;
    setDateRange({ end: endOfDay(new Date()) });
  };

  if (!hasValidGroupId) {
    return <div className="text-muted-foreground flex h-full w-full items-center justify-center p-4">Nieprawidłowy identyfikator grupy.</div>;
  }

  if (isError) {
    return <GroupPageErrorState errorMessage={errorMessage} />;
  }

  return (
    <div className="base-direction-col">
      <div className="bg-background border-border-primary flex h-full w-full flex-col gap-4 rounded-2xl border p-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/groups/${groupId}`}>Grupa</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Frekwencja</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <Button size="sm" variant="outline" className="gap-2" onClick={applyStartToTodayRange}>
              <CalendarIcon />
              Od początku do dziś
            </Button>
          </div>
          <SearchInput className="w-full max-w-xs" setValue={setGlobalFilter} />
        </div>

        {isPending ? <AttendanceTableSkeleton /> : <AttendanceTable table={table} />}
      </div>
    </div>
  );
}
